import { format } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isFulfilled } from "@reduxjs/toolkit";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { DialogBox } from "../../../common/components/functional/dialogBox/DialogBox";
import { CreditInvoiceRequest } from "../invoiceAPI";
import { fetchInvoicesByNumbers, selectAll } from "../invoiceDetailedSlice";
import { NewCreditInvoiceForm } from "./NewCreditInvoiceForm";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { createCreditInvoice } from "../invoiceSlice";
import { InvoiceSnackbar } from "../InvoiceSnackbar";
import CreditInvoiceSnackbarContent from "./CreditInvoiceSnackbarContent";
import usePreviewInvoices from "../hooks/usePreviewInvoices";
import {
  fetchCustomerOrgContracts,
  selectAllCustomerContracts,
} from "../../customer/customerContractsSlice";
import useIsLoading from "../../../hooks/useIsLoading";
import { isoFormat } from "../../../common/helpers/formatDateTime";

interface Props {
  isOpen: boolean;
  selectedInvoiceId: string;
  closeDialog: () => void;
}

export interface CreditInvoiceInfo {
  reason: string;
  freeText: string;
  invoiceValueDate: Date;
  paymentTermDays: string;
}

const DEFAULT_PAYMENT_TERM_DAYS = "7";

const INITIAL_CREDIT_INVOICE_INFO_DATA = {
  reason: "",
  freeText: "",
  invoiceValueDate: new Date(),
  paymentTermDays: DEFAULT_PAYMENT_TERM_DAYS,
};

export const NewCreditInvoiceDialog = ({
  isOpen,
  selectedInvoiceId,
  closeDialog,
}: Props) => {
  const { t } = useTranslation(["common", "invoices"]);
  const dispatch = useAppDispatch();
  const previewInvoices = usePreviewInvoices();

  const selectedInvoices = useAppSelector((state) => selectAll(state));
  const customerContracts = useAppSelector((state) =>
    selectAllCustomerContracts(state),
  );

  const areSlicesLoading = useIsLoading([
    "invoicesDetailed",
    "customerContracts",
  ]);
  const invoice = selectedInvoices?.find(
    (invoice) => invoice.invoiceNumber === selectedInvoiceId,
  );

  const [isCreatingCreditInvoice, setIsCreatingCreditInvoice] =
    useState<boolean>(false);

  const [createdInvoiceNumbers, setCreatedInvoiceNumbers] = useState<string[]>(
    [],
  );
  const [isSnackbarOpen, setIsSnackbarOpen] = useState<boolean>(false);

  const closeSnackbar = () => {
    setCreatedInvoiceNumbers([]);
    setIsSnackbarOpen(false);
  };

  const [creditInvoiceInfo, setCreditInvoiceInfo] = useState<CreditInvoiceInfo>(
    INITIAL_CREDIT_INVOICE_INFO_DATA,
  );

  const updateData = (name: string, value: any) => {
    setCreditInvoiceInfo({ ...creditInvoiceInfo, [name]: value });
  };

  const getPaymentTermDays = useCallback(
    (
      customerOrgId: string | undefined,
      contractId: string | undefined,
      eezyCompanyId: string | undefined,
    ) => {
      // If the invoice is internal invoice, we don't have customerOrganizationId and contractId
      // -> We use DEFAULT_PAYMENT_TERM_DAYS
      if (customerOrgId && contractId) {
        const contract = customerContracts.find(
          (contract) =>
            contract.id === contractId &&
            contract.eezyCompany.id === eezyCompanyId,
        );
        if (contract) {
          // Contract is already in redux -> use it
          setCreditInvoiceInfo((info) => {
            return {
              ...info,
              paymentTermDays:
                contract.paymentTermDays?.toString() ||
                DEFAULT_PAYMENT_TERM_DAYS,
            };
          });
          return;
        }
        // Customer contract not found, so fetch from backend
        dispatch(fetchCustomerOrgContracts(customerOrgId)).then((action) => {
          if (isFulfilled(action)) {
            const contracts = action.payload;
            const contract = contracts.find(
              (contract) => contract.id === contractId,
            );
            // If contract is not found or paymentTermDays is undefined, we use DEFAULT_PAYMENT_TERM_DAYS
            setCreditInvoiceInfo((info) => {
              return {
                ...info,
                paymentTermDays:
                  contract?.paymentTermDays?.toString() ||
                  DEFAULT_PAYMENT_TERM_DAYS,
              };
            });
          }
        });
      } else {
        // Internal invoice -> use default payment term days
        setCreditInvoiceInfo((info) => {
          return {
            ...info,
            paymentTermDays: DEFAULT_PAYMENT_TERM_DAYS,
          };
        });
      }
    },
    // If contract has been deleted and cannot be found, it causes indefinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch],
  );

  // Set correct payment term days when dialog is opened
  useEffect(() => {
    if (isOpen) {
      if (selectedInvoiceId && !invoice) {
        // Invoice is not in detailed slice -> fetch from backend
        dispatch(fetchInvoicesByNumbers([selectedInvoiceId])).then((action) => {
          if (isFulfilled(action)) {
            const invoiceDetails = action.payload.data[0];
            // Invoice details can be undefined
            // -> We use DEFAULT_PAYMENT_TERM_DAYS
            if (!invoiceDetails) return;
            const { customerOrganizationId, contractId, eezyCompanyId } =
              invoiceDetails;
            getPaymentTermDays(
              customerOrganizationId,
              contractId,
              eezyCompanyId,
            );
          }
        });
      } else if (selectedInvoiceId && invoice) {
        getPaymentTermDays(
          invoice.customerOrganizationId,
          invoice.contractId,
          invoice.eezyCompanyId,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, getPaymentTermDays, dispatch, selectedInvoiceId]);

  const cancelDialog = () => {
    closeDialog();
    setCreditInvoiceInfo(INITIAL_CREDIT_INVOICE_INFO_DATA);
  };

  const finishDialog = () => {
    const info: CreditInvoiceRequest = {
      originalInvoiceId: selectedInvoiceId,
      reason: !!creditInvoiceInfo.freeText
        ? creditInvoiceInfo.freeText
        : creditInvoiceInfo.reason,
      invoiceValueDate: format(creditInvoiceInfo.invoiceValueDate, isoFormat),
      paymentTermDays: creditInvoiceInfo.paymentTermDays,
    };

    setIsCreatingCreditInvoice(true); // Show spinner
    dispatch(createCreditInvoice(info)).then((action) => {
      setIsCreatingCreditInvoice(false); // Hide spinner
      if (isFulfilled(action)) {
        let invoiceNumbers: string[] = [];
        // Get new invoice numbers from events. Event.id === invoice number
        action.payload.forEach((event) => {
          if (event.name === "invNew") {
            invoiceNumbers.push(event.id);
          }
        });
        setCreatedInvoiceNumbers(invoiceNumbers);
        setIsSnackbarOpen(true);
        closeDialog();
      }
    });
  };

  const openInvoices = () => {
    previewInvoices(createdInvoiceNumbers);
  };

  const isLoading = isCreatingCreditInvoice || areSlicesLoading;

  const steps = [
    {
      component: (
        <>
          <OverlaySpinner isLoading={isLoading} />
          <NewCreditInvoiceForm
            creditInvoiceInfo={creditInvoiceInfo}
            updateData={updateData}
          />
        </>
      ),
    },
  ];

  return (
    <>
      <DialogBox
        isOpen={isOpen}
        dialogTitle={t("invoices:creditInvoice")}
        dialogInfo={t("invoices:creditInvoiceInfo") || ""}
        onCancelDialog={cancelDialog}
        onFinishDialog={finishDialog}
        steps={steps}
        cancelLabel={t("cancelShort")}
        finishLabel={t("invoices:createInvoices")}
      />
      <InvoiceSnackbar
        open={isSnackbarOpen}
        onClose={closeSnackbar}
        content={<CreditInvoiceSnackbarContent onClick={openInvoices} />}
      />
    </>
  );
};
